import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Localizer } from 'services/Localizer';
import { BulkAddToCartResponse } from 'services/UserData.Commerce';
import LazyImage from 'shared/assets/LazyImage';
import { AssetType } from 'shared/assets/types';
import ProductPricing from 'shared/components/commerce/ProductPricing';
import Modal from 'shared/components/Modal';
import RocOptionsTable from 'shared/components/roc-options-table/RocOptionsTable';
import RocOptionsTableTitle from 'shared/components/roc-options-table/RocOptionsTableTitle';
import Spinner from 'shared/components/Spinner';
import CheckCircleSVG from 'shared/components/svg/CheckCircleSVG';
import InlineAlert from 'shared/edit/InlineAlert';
import MultipleItemsAddedToCartAlert from '../store/MultipleItemsAddedToCartAlert';
import { toKebabCase } from 'util/StringUtil';
import { productAssetPlaceholder } from './ProductAssets';
import ProductSelectableAttributesList from './ProductSelectableAttributesList';
import BulkAddToList from './BulkAddToList';
import { ItemToAddToList } from '../my-account/shopping-lists/types';
import { dispatchRocEvent } from 'shared/hooks/useRocEventHandler';
import { GetPriceForQuantity, GetPricingLine } from '../pricing/pricing-result';
import { OrderMessageAction } from '../shopping-cart/OrderMessages';
import { AddToCartProps } from '../shopping-cart/types';
import useShoppingCart, { UseShoppingCart } from '../shopping-cart/useShoppingCart';
import { BulkProductsPartialDataResponse, getBulkPartialProductsData } from './data';
import { useProductDetailsContext } from './ProductDetailsContext';
import ProductPricingLines, { CheckableLine } from './ProductPricingLines';
import {
	AddToCartExceptionCode,
	Availability,
	ProductAsset,
	ProductDetailsState,
	ProductPricingLine,
	ProductPricingLinesDetails,
} from './types';

/**
 * Keys that will be requested from the server when requesting group product simples data
 */
const missingKeys: Array<keyof ProductDetailsState> = [
	'summary',
	'assets',
	'availability',
	'brand',
	'pricingLines',
	'displayPricing',
	'selections',
];

/**
 * Group Product Simples Listing Component
 */
export function SimpleProductsListing() {
	const {
		state: { productDetails, userIsSignedIn },
		actor: { isPurchaseable },
	} = useProductDetailsContext();

	const [selectedItems, setSelectedItems] = useState<AddToCartProps[]>([]);
	const [childrenData, setChildrenData] = useState<BulkProductsPartialDataResponse | null>(null);

	useEffect(() => {
		if (productDetails.children && productDetails.children?.length > 0) {
			async function fetchChildrenData(productIds: string[]) {
				const results = await getBulkPartialProductsData({
					productIds: productIds,
					missingKeys: missingKeys,
				});
				setChildrenData(results);
			}

			fetchChildrenData(productDetails.children.map((p) => p.id));
		} else {
			setChildrenData(null);
		}
	}, [productDetails.children]);

	const alternateProductDetailsDom = document.getElementById('alternate-product-details-simples');
	if (!alternateProductDetailsDom) {
		console.warn('alternate product details DOM element not found.');
		return null;
	}

	/**
	 * update the list of items addable to cart or to list when updating qty or unit of measure
	 */
	const updateSelectedItem = (itemId?: string, name?: string, selectedPricingLineId?: string, quantity?: number) => {
		if (!itemId) {
			return;
		}
		const itemIndex = selectedItems.findIndex((i) => i.itemId == itemId);

		if (!quantity || quantity === 0) {
			if (itemIndex > -1) {
				setSelectedItems(
					selectedItems?.filter(function (i) {
						return i.itemId !== itemId;
					}),
				);
			}
			return;
		}

		const newItems = [...selectedItems];
		if (itemIndex > -1) {
			newItems[itemIndex].quantity = quantity;
			newItems[itemIndex].productPricingLineId = selectedPricingLineId ?? null;
		} else {
			newItems.push({
				itemId: itemId,
				productPricingLineId: selectedPricingLineId ?? null,
				quantity: quantity,
			});
		}
		setSelectedItems(newItems);
	};

	const clearSelectedItems = () => setSelectedItems([]);

	const itemsToAddToList: ItemToAddToList[] = selectedItems.map((i) => {
		return {
			itemId: i.itemId,
			productPricingLineId: i.productPricingLineId,
			quantity: i.quantity ?? 1,
		};
	});

	const simplesListing = (
		<div data-testid="alternate-product-details-options">
			<RocOptionsTable>
				{childrenData
					? productDetails.children?.map((child, index) => {
							childrenData[child.id].id = child.id;
							return (
								<SimpleProductsListingRow
									updateSelectedItem={updateSelectedItem}
									item={childrenData[child.id]}
									index={index}
									key={index}
									selectedItems={selectedItems}
									isPurchaseable={isPurchaseable}
								/>
							);
					  })
					: null}
			</RocOptionsTable>
			<BulkAddToCartButton itemsToAdd={selectedItems} clearSelectedItems={clearSelectedItems} />
			<BulkAddToList
				itemsToAddToList={itemsToAddToList}
				isUserSignedIn={userIsSignedIn}
				clearSelectedItems={clearSelectedItems}
			/>
		</div>
	);

	return ReactDOM.createPortal(simplesListing, alternateProductDetailsDom);
}

interface SimpleProductsListingRowProps {
	index: number;
	item: Partial<ProductDetailsState>;
	updateSelectedItem: (
		itemId?: string,
		productname?: string,
		selectedPricingLineId?: string,
		quantity?: number | null,
	) => void;
	selectedItems: AddToCartProps[];
	isPurchaseable: (availability?: Availability | null, pricingLine?: ProductPricingLine) => boolean;
}

/**
 * Single simple product row
 */
function SimpleProductsListingRow(props: SimpleProductsListingRowProps) {
	const { item, index, updateSelectedItem, selectedItems, isPurchaseable } = props;
	const [selectedPricingLineId, setSelectedPricingLineId] = useState<string | undefined>();
	const [quantity, setQuantity] = useState<number | null>();

	const compareAssets = (a: ProductAsset, b: ProductAsset) => a.sortOrder - b.sortOrder;
	const defaultAsset = item?.assets?.sort(compareAssets).find((asset) => asset.assetType === AssetType.Image);
	const mainImage = defaultAsset ?? productAssetPlaceholder;

	useEffect(() => {
		const selectedPricingLineId = item.pricingLines && item.pricingLines.selected && item.pricingLines.selected.id;
		setSelectedPricingLineId(selectedPricingLineId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const getDetail = () => {
		if (item.displayPricing == null) {
			throw new Error("Display pricing was rendered when it wasn't ready!");
		}
		const displayPriceLine = GetPricingLine(item.displayPricing, item.id ?? '', selectedPricingLineId);

		return displayPriceLine && GetPriceForQuantity(displayPriceLine.prices, 1);
	};

	if (!item) {
		return null;
	}

	return (
		<tr key={index}>
			<td>
				{item.summary ? (
					<>
						<div className="roc-options-table__image-title-wrapper">
							{mainImage !== null ? (
								<div className="roc-options-table__image-title-wrapper-item">
									<div className="roc-options-table__image-wrapper">
										<LazyImage
											src={mainImage.mainImageUrl}
											alt={mainImage.altText}
											className="roc-options-table__image"
										/>
									</div>
								</div>
							) : null}
							<div className="roc-options-table__image-title-wrapper-item">
								<RocOptionsTableTitle data-testid={`rvp-header-${toKebabCase(item.summary.name)}`}>
									{item.summary.productUrl ? (
										<a
											href={item.summary.productUrl}
											// #region Commerce
											onClick={() => {
												if (item.id) {
													dispatchRocEvent('product-clicked', {
														productId: item.id,
														url: item.summary?.productUrl ?? '#',
														productName: item.summary?.name ?? '',
														productGroupId: item.summary?.productGroupId ?? null,
													});
												}
											}}
											// #endregion
										>
											<span>{item.summary.name}</span>
										</a>
									) : (
										<span>{item.summary.name}</span>
									)}
								</RocOptionsTableTitle>

								{item.summary.sku ? (
									<>
										{/* #region HCL - replace SKU for ItemNumber */}
										{Localizer('ItemNumber')}:{/* #endregion */}
										<span>{item.summary.sku}</span>
									</>
								) : null}

								<div data-testid={`selections-${toKebabCase(item.summary.name)}`}>
									<ProductSelectableAttributesList selections={item.selections} />
								</div>
							</div>
						</div>
					</>
				) : null}
			</td>

			<td>
				<ProductPricing
					displayPrice={getDetail()}
					className="roc-options-table__text roc-options-table__text--right"
					inline
					testId="pdp-main-price"
				/>

				<SimpleProductQtyUOM
					rowIndex={index}
					pricingLines={item.pricingLines}
					onPricingLineChange={async (pricingLineId: string) => {
						try {
							const selectedPricingLine =
								item.pricingLines &&
								item.pricingLines.available.find((line) => line.id === pricingLineId);

							if (!selectedPricingLine) {
								updateSelectedItem(item.id, item.summary?.name, undefined, quantity);
								throw new Error('Invalid unit of measure selected');
							}
							setSelectedPricingLineId(selectedPricingLine.id);
							updateSelectedItem(item.id, item.summary?.name, selectedPricingLineId, quantity);
						} catch (error) {
							console.error(error);
							updateSelectedItem(item.id, item.summary?.name, selectedPricingLineId);
						}
					}}
					onQuantityUpdate={(quantity) => {
						setQuantity(quantity);
						updateSelectedItem(item.id, item.summary?.name, selectedPricingLineId, quantity);
					}}
					selectedItems={selectedItems}
					itemId={item.id}
					itemName={item.summary?.name}
					isPurchaseable={isPurchaseable(item.availability, item.pricingLines?.selected)}
				/>
			</td>
		</tr>
	);
}

interface SimpleProductQtyUOMProps {
	rowIndex?: number;
	itemId?: string;
	itemName?: string;
	pricingLines?: ProductPricingLinesDetails;
	onPricingLineChange: (pricingLineId: string) => void;
	onQuantityUpdate?: (newQuantity: number | null) => void;
	selectedItems: AddToCartProps[];
	isPurchaseable: boolean;
}

/**
 * Simple products quantity and unit of measure inputs
 * those inputs will be used on adding multiple products to cart or to lists
 */
function SimpleProductQtyUOM(props: SimpleProductQtyUOMProps) {
	const [quantity, setQuantity] = useState<number | null>();
	const [selectedProductPricingLineId, setSelectedProductPricingLineId] = useState<string | undefined>();
	const {
		itemName,
		pricingLines,
		isPurchaseable,
		onPricingLineChange,
		onQuantityUpdate,
		selectedItems,
		itemId,
	} = props;

	//set quantity to null if the currenct item is no more selected
	useEffect(() => {
		if (!selectedItems || selectedItems.length == 0 || !selectedItems.find((e) => e.itemId == itemId)) {
			setQuantity(null);
		}
	}, [selectedItems, itemId]);

	const handleUnitOfMeasure = (line: CheckableLine) => {
		if (selectedProductPricingLineId === line.pricingLine?.id) {
			return;
		}
		setSelectedProductPricingLineId(line.pricingLine?.id);

		// trigger unit of measure change on the parent component only in case it is not an initialization process
		// because this will reload the data like pricing, availability, etc.
		if (!line.isInitialization && line.pricingLine) {
			onPricingLineChange(line.pricingLine.id);
		}
	};

	const updateQuantity = (e: React.ChangeEvent<HTMLInputElement>): void => {
		const newQuantity = parseInt(e.currentTarget.value);

		if (isNaN(newQuantity)) {
			setQuantity(null);
			if (onQuantityUpdate) {
				onQuantityUpdate(null);
			}
			return;
		}

		if (newQuantity > 0) {
			setQuantity(newQuantity);
			if (onQuantityUpdate) {
				onQuantityUpdate(newQuantity);
			}
		}
	};

	if (!isPurchaseable) {
		return null;
	}

	return (
		<div className="roc-options-table__form-group">
			{/* If a product doesn't have any units of measure it either cannot be purchased or it is a group product.
					Either way, we do not want to render the quantity controls in this case */}
			<label htmlFor="quantity" className="roc-options-table__form-label">
				<span className="roc-options-table__form-label-text">Qty:</span>
				<input
					name="quantity"
					type="number"
					autoComplete="off"
					id="quantity"
					tabIndex={-1}
					className="roc-form-input__input roc-options-table__form-input roc-options-table__form-input--small"
					data-testid={`alternate-layout-quantity-${toKebabCase(itemName ?? '')}`}
					value={quantity ?? ''}
					onChange={(e) => updateQuantity(e)}
				/>
			</label>
			{pricingLines && pricingLines.available && pricingLines.available.length > 0 ? (
				<ProductPricingLines
					className="roc-options-table__form-label"
					details={pricingLines}
					onPricingLineChange={handleUnitOfMeasure}
				/>
			) : null}
		</div>
	);
}

interface BulkAddToCartButtonProps {
	itemsToAdd: AddToCartProps[];
	clearSelectedItems: () => void;
}

/**
 * Add to cart button that will be used to add multiple items to cart at once
 */
function BulkAddToCartButton(props: BulkAddToCartButtonProps) {
	const { itemsToAdd, clearSelectedItems } = props;
	const [modalOpen, setModalOpen] = useState(true);
	const [addingToCart, setAddingToCart] = useState(false);
	const [isViewCartActive, setIsViewCartActive] = useState<boolean>(false);
	const [errorMessages, setErrorMessages] = useState<Array<string>>([]);

	const [bulkAddToCartResponse, setBulkAddToCartResponse] = useState<BulkAddToCartResponse | null>(null);

	const UseShoppingCartResult: UseShoppingCart = useShoppingCart();
	const {
		state: { error, errorType },
		actor: { bulkAddToCart },
	} = UseShoppingCartResult;

	const errorTitle =
		errorType === AddToCartExceptionCode.cartFull
			? Localizer('CartFull')
			: errorType === AddToCartExceptionCode.productOutOfStock
			? Localizer('OutOfStock')
			: errorType === AddToCartExceptionCode.productUnavailable
			? Localizer('ProductUnavailable')
			: Localizer('ErrorMessageGenericTitle');

	const handleBulkAddToCart = async () => {
		setErrorMessages([]);
		setBulkAddToCartResponse(null);

		try {
			setAddingToCart(true);
			const response = await bulkAddToCart({ items: itemsToAdd });

			if (!response) {
				setBulkAddToCartResponse(null);
				return;
			}

			setBulkAddToCartResponse(response);
			clearSelectedItems();

			const orderErrorMessages = response.orderMessages?.filter(
				(om) =>
					om.messageAction === OrderMessageAction.Error ||
					om.messageAction === OrderMessageAction.RedirectToCart,
			);

			if (orderErrorMessages && orderErrorMessages.length > 0) {
				setErrorMessages(orderErrorMessages.map((orderMessages) => orderMessages.message));
				setModalOpen(false);
				setIsViewCartActive(true);
			} else {
				setModalOpen(true);
			}

			dispatchRocEvent('roc-cart-summary-updated', { cartSummary: response.cartSummary });
		} catch (error) {
			setBulkAddToCartResponse(null);
		}

		setAddingToCart(false);
	};

	return (
		<>
			<button
				onClick={() => handleBulkAddToCart()}
				type="button"
				className="roc-btn roc-btn--block roc-btn--big"
				disabled={
					addingToCart ||
					!itemsToAdd ||
					itemsToAdd.length == 0 ||
					itemsToAdd.every((e) => e.productPricingLineId == undefined)
				}
				data-testid="add-to-cart-button"
			>
				{Localizer('AddToCart')}
				{addingToCart && <Spinner />}
			</button>

			{bulkAddToCartResponse !== null ? (
				<Modal
					isOpen={modalOpen}
					renderTitle={() => Localizer('AddedToCart')}
					closeModal={() => setModalOpen(false)}
					titleIcon={<CheckCircleSVG />}
				>
					{<MultipleItemsAddedToCartAlert response={bulkAddToCartResponse} />}
				</Modal>
			) : null}

			{error != null || errorMessages.length > 0 ? (
				<InlineAlert
					theme="danger"
					isTitleOnOwnRow
					title={errorTitle}
					message={
						<>
							{errorMessages.map((orderMessage, index) => (
								<div key={index}>
									<span dangerouslySetInnerHTML={{ __html: orderMessage }} />
									<br />
								</div>
							))}
							<br /> <br />
							{isViewCartActive ? (
								<a
									href="/cart"
									className="roc-btn roc-btn-danger"
									data-testid="add-to-cart-error-view-cart-link"
								>
									{Localizer('ViewCart')}
								</a>
							) : null}
						</>
					}
					error={error}
				/>
			) : null}
		</>
	);
}
